import React, {useState} from "react";
import {useGetTicketByUniqueCode} from "../../hooks/Administrative/useGetTicketByUniqueCode";
import "./TicketInfo.css";
import {format} from "date-fns";
import {es} from "date-fns/locale/es";
import {useUpdateTicketState} from "../../hooks/Administrative/useUpdateTicketState";
import {Bounce, toast} from "react-toastify";
import {useDownloadTicketFiles} from "../../hooks/Administrative/useDownloadTicketFiles";

const ticketStateMap: Map<string, string> = new Map([
    ["CREATED", "Creado"],
    ["APPROVED", "Aprobado"],
    ["ASSIGNED", "Asignado"],
    ["IN_PROGRESS", "En progreso"],
    ["BLOCKED", "Bloqueado"],
    ["IN_REVIEW", "En revisión"],
    ["COMPLETED", "Completado"],
]);

const serviceTypeMap: Map<string, string> = new Map([
    ["PREVENTIVE", "Preventivo"],
    ["CORRECTIVE", "Correctivo"],
    ["INSTALLATION", "Instalación"],
    ["VERIFICATION", "Verificación"],
]);

const TicketInfo = ({additionalInfo, ticketCode, onBack, showTicketInfo}: {
    additionalInfo: any;
    ticketCode: string;
    onBack: () => void;
    showTicketInfo: (show: boolean) => void;
}) => {
    const {ticket} = useGetTicketByUniqueCode(ticketCode);
    const [isModalVisible, setModalVisible] = useState(false);
    const [isCancelModalVisible, setCancelModalVisible] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const updateState = useUpdateTicketState()
    const {filesToDownload, getFiles} = useDownloadTicketFiles();

    const formattedDate = ticket?.createdAt
        ? format(new Date(ticket.createdAt.replace(" ", "T")), "dd MMMM yyyy, HH:mm:ss", {locale: es})
        : "N/A";

    const handleAcceptTicket = () => {
        setModalVisible(true);
    };

    const handleCancelTicket = () => {
        setCancelModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setCancelModalVisible(false);
        setCancelReason('')
    };

    const confirmAcceptTicket = () => {
        updateState.updateState("Aprobado", ticketCode, '').then((response) => {
            toast.success("Ticket aprobado", {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        }).catch((error) => {
            toast.error(error.message, {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        });
        ticket.ticketState = "APPROVED"
        additionalInfo['ESTADO'] = 'Aprobado'
        setModalVisible(false);
    };


    const confirmCancelTicket = () => {
        updateState.updateState("Cancelado", ticketCode, cancelReason).then((response) => {
            toast.success("Ticket rechazado", {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        }).catch((error) => {
            toast.error(error.message, {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        });
        setModalVisible(false);
        setCancelModalVisible(false);
        showTicketInfo(false);
        additionalInfo['ESTADO'] = 'Rechazado'
    };

    const downloadTicketFiles = () => {
        getFiles(ticketCode).then(response => {
            if (!response.files || response.files.length === 0) {
                console.log(response.files);
                toast.warning(
                    'No hay archivos encontrados para ticket',
                    {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: false,
                        theme: 'colored',
                        transition: Bounce,
                    }
                );
                return;
            }

            response.files.forEach((fileUrl: string, index: number) => {
                downloadFile(fileUrl, `${ticketCode}-${index + 1}`);
            });
        })
            .catch((error) => {
                toast.error("Error al obtener archivos para descargar", {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    theme: "colored",
                    transition: Bounce,
                });
            });
    };


    const downloadFile = async (presignedUrl: string, fileName: string) => {
        try {
            const response = await fetch(presignedUrl);

            if (!response.ok) {
                toast.error("Error al descargar archivo", {
                    position: "top-right",
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: false,
                    theme: "colored",
                    transition: Bounce,
                })
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = fileName || "download";
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Error al buscar archivo para descargar", {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: false,
                theme: "colored",
                transition: Bounce,
            })
        }
    };

    return (
        <div className="tickets-info-view-container">
            <div className="tickets-info-view-header">
                <button onClick={onBack}>REGRESAR</button>
                <div className="tickets-info-view-header-title">
                    <p>TICKET</p>
                </div>
                {ticket?.ticketState === "CREATED" && (
                    <>
                        <span className="material-icons-outlined icon-unselected" onClick={handleAcceptTicket}>
                            check
                        </span>
                        <span className="material-icons-outlined icon-unselected-cancel" onClick={handleCancelTicket}>
                            cancel
                        </span>
                    </>
                )}
                {ticket?.ticketState === "COMPLETED" && (
                    <span className="material-icons-outlined icon-unselected" onClick={downloadTicketFiles}>
                        download_for_offline
                    </span>
                )}
            </div>
            <div className="admin-tickets-info-wrapper">
                <div className="admin-tickets-info-view-body-info">
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">CLAVE DE TICKET</div>
                        <div className="value-label">{ticketCode}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">TIPO DE SERVICIO</div>
                        <div className="value-label">{serviceTypeMap.get(ticket?.service)}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">ESTADO</div>
                        <div className="value-label">{ticketStateMap.get(ticket?.ticketState)}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">DESCRIPCION</div>
                        <div className="value-label">{ticket?.description}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">COMENTARIO ADICIONAL</div>
                        <div className="value-label">{ticket?.additionalComment}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">TÉCNICO ASIGNADO</div>
                        <div className="value-label">{ticket?.assignedTechnician}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">FECHA DE CREACIÓN</div>
                        <div className="value-label">{formattedDate}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">CLIENTE</div>
                        <div className="value-label">{additionalInfo.CLIENTE}</div>
                    </div>
                    <div className="ticket-info-attribute">
                        <div className="attribute-label">PLANTA</div>
                        <div className="value-label">{additionalInfo.PLANTA}</div>
                    </div>
                </div>
                <div className="admin-tickets-additional-info">
                    <div className="ticket-additional-info-attribute">
                        <div className="attribute-label">EQUIPOS</div>
                        <div>
                            <ul className="additional-info-attribute-list">
                                {ticket?.equipments?.map((equipment: any, index: any) => (
                                    <li key={index}>
                                        {equipment}
                                    </li>))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {isModalVisible && (
                <div className="modal-overlay">
                    <div className="modal">
                        <p>¿Está seguro de que desea aceptar este ticket?</p>
                        <div className="modal-buttons">
                            <button onClick={confirmAcceptTicket}>Aceptar</button>
                            <button onClick={closeModal}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {isCancelModalVisible && (
                <div className="modal-overlay">
                    <div className="modal">
                        <p>¿Está seguro de que desea rechazar este ticket?</p>
                        <p>Una vez rechazado, no se podrá interactuar con él más, y se borrará del sistema.</p>
                        <textarea
                            placeholder="Razón de cancelación (opcional)"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            rows={4}
                        />
                        <div className="modal-buttons">
                            <button onClick={confirmCancelTicket}>Confirmar Cancelación</button>
                            <button onClick={closeModal}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketInfo;
